import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Loading, ErrorState, EmptyState, StatCard } from "@/components/common";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { ArrowLeft, Clock, Dumbbell, Plus, Trash2, ClipboardList, Shield, MessageSquare, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import ProgramCalendar from "@/components/ProgramCalendar";
import { DiaryTrendChart } from "@/components/DiaryTrendChart";

const asList = (d) => (Array.isArray(d) ? d : d?.items || d?.exercises || d?.results || []);
const PROFILE_FIELDS = ["category", "birth_date", "sex", "height_cm", "weight_kg", "sport_activity", "scoliosis_type", "main_curve", "convexity_side", "cobb_angle", "risser", "pain_present", "pain_nrs", "wears_brace", "clinical_notes", "therapy_goals"];

export default function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [assist, setAssist] = useState(null);
  const [pstats, setPstats] = useState(null);
  const [allExercises, setAllExercises] = useState([]);
  const [program, setProgram] = useState(null);
  const [braceList, setBraceList] = useState([]);
  const [braceSummary, setBraceSummary] = useState(null);
  const [diaryList, setDiaryList] = useState([]);
  const [prescribed, setPrescribed] = useState(null);
  const [prescInput, setPrescInput] = useState("");
  const [wearsBrace, setWearsBrace] = useState(false);
  const [savingBrace, setSavingBrace] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [p, a, ex, st, pr, br, pres, dia] = await Promise.allSettled([
        api.patient(id), api.patientAssistance(id), api.exercises(), api.patientStats(id),
        api.patientProgram(id), api.braceLogs(id), api.getBracePrescription(id), api.diary(id),
      ]);
      if (p.status === "fulfilled") { setPatient(p.value); setWearsBrace(!!p.value?.profile?.wears_brace); } else setError(errMsg(p.reason));
      if (a.status === "fulfilled") setAssist(a.value);
      if (ex.status === "fulfilled") setAllExercises(asList(ex.value));
      if (st.status === "fulfilled") setPstats(st.value);
      if (pr.status === "fulfilled") setProgram(pr.value);
      if (dia.status === "fulfilled") setDiaryList(asList(dia.value));
      if (br.status === "fulfilled") { setBraceList(br.value?.logs || []); setBraceSummary(br.value?.summary || null); }
      if (pres.status === "fulfilled") { setPrescribed(pres.value?.prescribed_hours ?? null); setPrescInput(pres.value?.prescribed_hours != null ? String(pres.value.prescribed_hours) : ""); }
    } catch (e) { setError(errMsg(e)); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const toggleBrace = async (val) => {
    setSavingBrace(true);
    try {
      const payload = {};
      PROFILE_FIELDS.forEach((f) => { payload[f] = patient?.profile?.[f] ?? null; });
      payload.wears_brace = val;
      await api.updatePatientProfile(id, payload);
      setWearsBrace(val);
      toast.success(val ? "Segnato come portatore di busto" : "Portatore di busto disattivato");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSavingBrace(false); }
  };

  const savePrescription = async () => {
    const h = parseFloat(prescInput);
    if (isNaN(h) || h < 0 || h > 24) { toast.error("Inserisci ore valide (0-24)"); return; }
    setSavingBrace(true);
    try {
      await api.setBracePrescription(id, h);
      setPrescribed(h);
      toast.success("Ore prescritte salvate");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSavingBrace(false); }
  };

  if (loading) return <Loading />;  if (error) return <ErrorState message={error} onRetry={load} />;

  const p = patient || {};
  const name = p.full_name || p.username;
  const initials = (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const minutesVal = assist?.executed_total_minutes ?? assist?.executed_minutes ?? assist?.total_minutes ?? assist?.minutes;
  const minutes = typeof minutesVal === "number" ? minutesVal : "—";

  const diaryTrend = [...diaryList]
    .filter((d) => d.created_at)
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
    .map((d) => ({
      label: new Date(d.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" }),
      pain: typeof d.pain_nrs === "number" ? d.pain_nrs : null,
      exertion: typeof d.difficulty === "number" ? d.difficulty : null,
    }));

  const removePatient = async () => {
    try {
      await api.deletePatient(id);
      toast.success("Paziente eliminato");
      navigate("/physio/patients");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4" data-testid="back-button">
        <ArrowLeft className="mr-2 h-4 w-4" /> Pazienti
      </Button>

      <Card className="mb-6 border-border/70">
        <CardContent className="flex flex-wrap items-center gap-4 p-6">
          <Avatar className="h-16 w-16 bg-primary/15"><AvatarFallback className="bg-transparent text-lg font-semibold text-primary">{initials}</AvatarFallback></Avatar>
          <div className="flex-1">
            <h1 className="text-2xl font-semibold">{name}</h1>
            <p className="text-sm text-muted-foreground">@{p.username}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {p.category && <Badge variant="secondary">{p.category}</Badge>}
              {p.access_expires_at
                ? <Badge variant="outline">Scade {new Date(p.access_expires_at).toLocaleDateString("it-IT")}</Badge>
                : <Badge variant="outline">Senza scadenza</Badge>}
            </div>
          </div>
          <StatCard icon={Clock} label="Minuti assistenza" value={minutes} testId="patient-minutes" />
          <Button variant="outline" onClick={() => navigate(`/physio/chat?patient=${id}`)} data-testid="patient-open-chat">
            <MessageSquare className="mr-2 h-4 w-4" /> Chat
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="text-destructive hover:text-destructive" data-testid="delete-patient-detail-button">
                <Trash2 className="mr-2 h-4 w-4" /> Elimina paziente
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader><AlertDialogTitle>Eliminare il paziente?</AlertDialogTitle><AlertDialogDescription>Il profilo di {name} verrà eliminato definitivamente insieme ai suoi dati. Questa azione non può essere annullata.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel>Annulla</AlertDialogCancel><AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={removePatient}>Elimina</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>

      {/* Andamento ultimi 7 giorni */}
      <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Andamento (ultimi 7 giorni)</p>
      <div className="mb-8 grid gap-4 sm:grid-cols-2">
        <div className="rounded-2xl bg-[#e6f0e9] p-5" data-testid="patient-sessions">
          <p className="text-3xl font-bold text-[#1f3a5f]">{pstats?.sessions_last_7 ?? "—"}</p>
          <p className="text-sm text-[#1f3a5f]/70">Sessioni completate</p>
        </div>
        <div className="rounded-2xl bg-[#fbe9db] p-5" data-testid="patient-adherence">
          <p className="text-3xl font-bold text-[#1f3a5f]">{typeof pstats?.adherence_pct === "number" ? `${Math.round(pstats.adherence_pct)}%` : "—"}</p>
          <p className="text-sm text-[#1f3a5f]/70">Aderenza media</p>
        </div>
      </div>

      {diaryTrend.length > 0 && (
        <Card className="mb-8 border-border/70" data-testid="diary-trend-card">
          <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><TrendingUp className="h-5 w-5 text-primary" /> Andamento dolore e sforzo</CardTitle></CardHeader>
          <CardContent>
            <p className="mb-3 text-sm text-muted-foreground">Dolore (scala NRS 0–10) e sforzo percepito (scala Borg 0–10) registrati dal paziente nel diario.</p>
            <DiaryTrendChart data={diaryTrend} testId="diary-trend-chart" />
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="program">
        <TabsList data-testid="patient-tabs">
          <TabsTrigger value="program">Costruttore programma</TabsTrigger>
          <TabsTrigger value="brace" data-testid="tab-brace">Busto</TabsTrigger>
          <TabsTrigger value="info">Informazioni</TabsTrigger>
        </TabsList>

        <TabsContent value="program" className="mt-4">
          <p className="mb-4 text-sm text-muted-foreground">Tocca un giorno del mese per assegnare gli esercizi.</p>
          <ProgramCalendar patientId={id} exercises={allExercises} program={program} braceLogs={braceList} diaryLogs={diaryList} onSaved={load} />
        </TabsContent>

        <TabsContent value="brace" className="mt-4">
          <Card className="mb-4 border-border/70">
            <CardContent className="space-y-5 p-6">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="grid h-10 w-10 place-items-center rounded-xl bg-primary/10 text-primary"><Shield className="h-5 w-5" /></div>
                  <div>
                    <p className="font-semibold">Portatore di busto</p>
                    <p className="text-xs text-muted-foreground">Attiva se il paziente deve indossare il busto ortopedico.</p>
                  </div>
                </div>
                <Switch checked={wearsBrace} disabled={savingBrace} onCheckedChange={toggleBrace} data-testid="brace-wears-switch" />
              </div>

              {wearsBrace && (
                <div className="flex flex-wrap items-end gap-3 border-t border-border/60 pt-5">
                  <div className="flex-1 min-w-[180px]">
                    <Label>Ore prescritte al giorno</Label>
                    <Input type="number" min="0" max="24" step="0.5" value={prescInput} onChange={(e) => setPrescInput(e.target.value)} placeholder="es. 12" className="mt-1.5" data-testid="brace-prescribed-input" />
                  </div>
                  <Button onClick={savePrescription} disabled={savingBrace} data-testid="brace-prescribed-save">Salva ore prescritte</Button>
                </div>
              )}
            </CardContent>
          </Card>

          {wearsBrace ? (
            <>
              <div className="mb-4 grid gap-4 sm:grid-cols-3">
                <div className="rounded-2xl bg-[#e6f0e9] p-5" data-testid="brace-report-avg">
                  <p className="text-3xl font-bold text-[#1f3a5f]">{braceSummary?.avg_hours != null ? `${braceSummary.avg_hours}h` : "—"}</p>
                  <p className="text-sm text-[#1f3a5f]/70">Media indossate/die</p>
                </div>
                <div className="rounded-2xl bg-[#fbe9db] p-5" data-testid="brace-report-7d">
                  <p className="text-3xl font-bold text-[#1f3a5f]">{braceSummary?.avg_hours_7d != null ? `${braceSummary.avg_hours_7d}h` : "—"}</p>
                  <p className="text-sm text-[#1f3a5f]/70">Media ultimi 7 giorni</p>
                </div>
                <div className="rounded-2xl bg-[#e6f0e9] p-5" data-testid="brace-report-adherence">
                  <p className="text-3xl font-bold text-[#1f3a5f]">{prescribed && braceSummary?.avg_hours != null ? `${Math.round((braceSummary.avg_hours / prescribed) * 100)}%` : "—"}</p>
                  <p className="text-sm text-[#1f3a5f]/70">Aderenza (media/prescritte)</p>
                </div>
              </div>

              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Registrazioni giornaliere (indossate / prescritte)</p>
              {braceList.length === 0 ? (
                <EmptyState title="Nessuna registrazione" subtitle="Il paziente non ha ancora registrato l'utilizzo del busto." />
              ) : (
                <div className="grid gap-3 sm:grid-cols-2" data-testid="brace-daily-list">
                  {braceList.map((it, i) => (
                    <Card key={it.date || i} className="border-border/70">
                      <CardContent className="flex items-center gap-4 p-4">
                        <div className="grid h-11 w-11 place-items-center rounded-xl bg-primary/10 text-primary"><Shield className="h-5 w-5" /></div>
                        <div>
                          <p className="text-lg font-bold">{it.hours ?? "—"}<span className="text-sm font-normal text-muted-foreground">/{prescribed ?? "—"} ore</span></p>
                          <p className="flex items-center gap-1 text-xs text-muted-foreground"><Clock className="h-3 w-3" />{new Date(it.date).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </>
          ) : (
            <EmptyState title="Paziente non portatore di busto" subtitle="Attiva l'opzione sopra per abilitare il monitoraggio del busto." />
          )}
        </TabsContent>

        <TabsContent value="info" className="mt-4">
          <Card className="border-border/70">
            <CardContent className="grid gap-3 p-6 sm:grid-cols-2">
              {Object.entries(p)
                .filter(([k, v]) => !["access_code"].includes(k) && v != null && typeof v !== "object")
                .map(([k, v]) => (
                  <div key={k} className="rounded-lg bg-muted/50 p-3">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">{k.replace(/_/g, " ")}</p>
                    <p className="mt-0.5 text-sm font-medium break-words">
                      {typeof v === "boolean" ? (v ? "Sì" : "No") : String(v)}
                    </p>
                  </div>
                ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
